import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { CalendarDays, Users, FileText, MessageSquare, Eye, TrendingUp } from "lucide-react";
import { api } from "../../api";

const COLORS = ["#E63946", "#FF8C8C", "#FFB6B6", "#71717A"];

function StatCard({ icon: Icon, label, value, hint, accent }) {
  return (
    <div className={`rounded-lg border p-5 ${accent ? "border-[#e63946]/40 bg-[#1a0a0d]" : "border-white/10 bg-[#0f0f0f]"}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-white/50">{label}</span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-md ${accent ? "bg-brand-gradient text-white" : "bg-white/5 text-white/70"}`}>
          <Icon size={15} />
        </span>
      </div>
      <p className="mt-3 font-head text-3xl font-semibold text-white">{value}</p>
      {hint && <p className="mt-1 text-xs text-white/50">{hint}</p>}
    </div>
  );
}

export default function Overview() {
  const [data, setData] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/stats");
    setData(data);
  };
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);

  if (!data) return <div className="text-white/50" data-testid="overview-loading">Chargement…</div>;

  const c = data.counters;
  return (
    <div className="space-y-8" data-testid="overview">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Eye} label="Visites totales" value={c.visits} hint="Toutes pages confondues" />
        <StatCard icon={MessageSquare} label="Demandes" value={c.quotes} hint={c.quotes_new ? `${c.quotes_new} non lue(s)` : "Aucune non lue"} accent={c.quotes_new > 0} />
        <StatCard icon={CalendarDays} label="Rendez-vous" value={c.appointments} hint={c.appointments_new ? `${c.appointments_new} nouveau(x)` : "À jour"} accent={c.appointments_new > 0} />
        <StatCard icon={FileText} label="Devis créés" value={c.devis} hint="Émis à ce jour" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-lg border border-white/10 bg-[#0f0f0f] p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="font-head text-lg font-medium uppercase tracking-tight text-white">Visites — 14 derniers jours</h3>
              <p className="text-xs text-white/50">Trafic quotidien du site public</p>
            </div>
            <span className="flex items-center gap-1 text-xs text-white/60"><TrendingUp size={14} className="text-[#ff4d4d]" /> Live</span>
          </div>
          <div className="h-64 min-h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="brandFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#e63946" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#e63946" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1f1f1f" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fill: "#71717A", fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", color: "#fff" }} />
                <Area type="monotone" dataKey="visits" stroke="#ff4d4d" fill="url(#brandFill)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-white/10 bg-[#0f0f0f] p-6">
          <h3 className="font-head text-lg font-medium uppercase tracking-tight text-white">Répartition</h3>
          <p className="text-xs text-white/50">Catégories des demandes</p>
          <div className="mt-4 h-64 min-h-[240px]">
            {data.categories.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-white/40">
                Aucune donnée pour le moment
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.categories} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={4}>
                    {data.categories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", color: "#fff" }} />
                  <Legend wrapperStyle={{ color: "#a1a1aa", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
